'use client';

import { type ComponentPropsWithoutRef, useEffect, useRef } from 'react';

import { createEntranceController } from './landing-entrance';
import { splitHeadingLetters } from './landing-heading-letters';
import { landingParallaxTransform } from './landing-motion';

type RevealTarget = {
  effect: 'fade' | 'rise';
  expressive: boolean;
  stagger: boolean;
  section: HTMLElement;
};

type ParallaxTarget = {
  frame: HTMLElement;
  strength: 'subtle' | 'strong';
  transform: string;
  willChange: string;
  transitionProperty: string;
};

export function LandingMotionRoot({ children, ...props }: ComponentPropsWithoutRef<'div'>) {
  const rootRef = useRef<HTMLDivElement>(null);
  const revealed = useRef(new WeakSet<Element>());
  const lettered = useRef(new WeakSet<Element>());

  useEffect(() => {
    const root = rootRef.current;
    const view = root?.ownerDocument.defaultView;
    if (!root || !view || !view.IntersectionObserver || !view.matchMedia) return;
    const document = root.ownerDocument;
    const preference = view.matchMedia('(prefers-reduced-motion: reduce)');
    let stop = () => {};

    const configure = () => {
      stop();
      if (preference.matches) return;

      const targets = new Map<HTMLElement, RevealTarget>();
      const images = new Map<HTMLElement, ParallaxTarget>();
      const nearby = new Set<HTMLElement>();
      const animations = new Map<HTMLElement, Animation>();
      const headings = new Set<HTMLElement>();
      const letterRuns = new Map<HTMLElement, () => void>();
      const entrance = createEntranceController(root!, view);
      let frame = 0;
      let alive = true;
      let needsReconcile = false;

      const restoreImage = (image: HTMLElement, original: ParallaxTarget) => {
        image.style.transform = original.transform;
        image.style.willChange = original.willChange;
        image.style.transitionProperty = original.transitionProperty;
      };

      const updateParallax = () => {
        frame = 0;
        if (!alive || document.hidden) return;
        if (needsReconcile) {
          needsReconcile = false;
          reconcile();
        }
        const updates = [...nearby].flatMap((image) => {
          const target = images.get(image);
          if (!target || !image.isConnected) return [];
          const rect = target.frame.getBoundingClientRect();
          return [{ image, transform: landingParallaxTransform({ top: rect.top, height: rect.height, viewportHeight: view.innerHeight, viewportWidth: view.innerWidth, strength: target.strength }) }];
        });
        for (const { image, transform } of updates) {
          image.style.willChange = 'transform';
          image.style.transitionProperty = 'opacity, scale';
          image.style.transform = transform;
        }
      };

      const schedule = () => {
        if (alive && !document.hidden && !frame && (needsReconcile || nearby.size > 0)) frame = view.requestAnimationFrame(updateParallax);
      };

      const reveals = new view.IntersectionObserver((entries) => {
        const staggerCounts = new Map<HTMLElement, number>();
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const element = entry.target as HTMLElement;
          const target = targets.get(element);
          if (!target || revealed.current.has(element)) continue;
          revealed.current.add(element);
          reveals.unobserve(element);
          if (document.hidden || element.contains(document.activeElement) || !element.animate) continue;
          const count = staggerCounts.get(target.section) ?? 0;
          if (target.stagger) staggerCounts.set(target.section, count + 1);
          const delay = target.stagger ? Math.min(count * (target.expressive ? 90 : 65), 270) : 0;
          try {
            const animation = element.animate([
              { opacity: 0, translate: target.effect === 'rise' ? `0 ${target.expressive ? 28 : 14}px` : '0 0' },
              { opacity: view.getComputedStyle(element).opacity, translate: '0 0' },
            ], { duration: target.expressive ? 650 : 440, delay, easing: 'cubic-bezier(0.22, 1, 0.36, 1)', fill: 'backwards' });
            animations.set(element, animation);
            animation.onfinish = () => { animations.delete(element); animation.cancel(); };
          } catch {
            animations.get(element)?.cancel();
            animations.delete(element);
          }
        }
      }, { rootMargin: '0px 0px -24px 0px', threshold: 0 });

      // Mirrors the source theme's heading entrance: letters slide in from 50px with a back-out ease, 20ms apart.
      const animateHeading = (heading: HTMLElement) => {
        const split = splitHeadingLetters(heading);
        if (!split) return;
        if (!split.parts[0]?.animate) { split.revert(); return; }
        const shift = view.getComputedStyle(heading).direction === 'rtl' ? -50 : 50;
        const step = Math.min(split.byWord ? 80 : 20, 1200 / split.parts.length);
        let runs: Animation[] = [];
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          runs.forEach((run) => run.cancel());
          split.revert();
          letterRuns.delete(heading);
        };
        try {
          runs = split.parts.map((part, index) => part.animate([
            { opacity: 0, translate: `${shift}px 0` },
            { opacity: 1, translate: '0 0' },
          ], { duration: 1000, delay: index * step, easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)', fill: 'backwards' }));
        } catch {
          finish();
          return;
        }
        letterRuns.set(heading, finish);
        void Promise.all(runs.map((run) => run.finished)).then(finish, finish);
      };

      const headingReveals = new view.IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const heading = entry.target as HTMLElement;
          headingReveals.unobserve(heading);
          if (lettered.current.has(heading)) continue;
          lettered.current.add(heading);
          if (!document.hidden) animateHeading(heading);
        }
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });

      const parallax = new view.IntersectionObserver((entries) => {
        for (const entry of entries) {
          const image = entry.target as HTMLElement;
          if (entry.isIntersecting) nearby.add(image);
          else {
            nearby.delete(image);
            const original = images.get(image);
            if (original) restoreImage(image, original);
          }
        }
        schedule();
      }, { rootMargin: '100px 0px', threshold: 0 });

      function reconcile() {
        const nextTargets = new Map<HTMLElement, RevealTarget>();
        const nextImages = new Map<HTMLElement, { frame: HTMLElement; strength: 'subtle' | 'strong' }>();
        const nextHeadings = new Set<HTMLElement>();
        for (const section of root!.querySelectorAll<HTMLElement>('[data-block]')) {
          const effect = section.dataset.motionReveal;
          const stagger = section.dataset.motionStagger === 'true';
          const expressive = section.dataset.motionIntensity === 'expressive';
          if (effect === 'fade' || effect === 'rise') {
            const cards = stagger ? [...section.querySelectorAll<HTMLElement>('[data-motion-item], .landing-card')].filter((item) => !item.parentElement?.closest('[data-motion-item], .landing-card')) : [];
            const elements = cards.length ? [...section.querySelectorAll<HTMLElement>('.landing-section-header'), ...cards] : [section];
            for (const element of elements) nextTargets.set(element, { effect: effect === 'rise' ? 'rise' : 'fade', expressive, stagger: cards.includes(element), section });
          }
          if (section.dataset.motionHeading === 'letters' && section.dataset.block !== 'hero') {
            for (const heading of section.querySelectorAll<HTMLElement>('h1, h2')) {
              if (!heading.closest('[data-motion-item], .landing-card')) nextHeadings.add(heading);
            }
          }
          const strength = section.dataset.motionParallax;
          if (strength === 'subtle' || strength === 'strong') {
            for (const image of section.querySelectorAll<HTMLElement>('[data-motion-image]')) {
              if (image.parentElement) nextImages.set(image, { frame: image.parentElement, strength });
            }
          }
        }
        for (const [element] of targets) {
          if (nextTargets.has(element)) continue;
          reveals.unobserve(element);
          animations.get(element)?.cancel();
          animations.delete(element);
          targets.delete(element);
        }
        for (const [element, target] of nextTargets) {
          const existing = targets.has(element);
          targets.set(element, target);
          if (existing || revealed.current.has(element)) continue;
          const rect = element.getBoundingClientRect();
          if (rect.top < view!.innerHeight && rect.bottom > 0) revealed.current.add(element);
          else reveals.observe(element);
        }
        for (const [image, original] of images) {
          if (nextImages.has(image)) continue;
          parallax.unobserve(image);
          nearby.delete(image);
          restoreImage(image, original);
          images.delete(image);
        }
        for (const [image, target] of nextImages) {
          const existing = images.get(image);
          if (existing) { existing.strength = target.strength; existing.frame = target.frame; continue; }
          images.set(image, { ...target, transform: image.style.transform, willChange: image.style.willChange, transitionProperty: image.style.transitionProperty });
          parallax.observe(image);
        }
        for (const heading of headings) {
          if (nextHeadings.has(heading)) continue;
          headingReveals.unobserve(heading);
          letterRuns.get(heading)?.();
          headings.delete(heading);
        }
        for (const heading of nextHeadings) {
          if (headings.has(heading)) continue;
          headings.add(heading);
          if (lettered.current.has(heading)) continue;
          const rect = heading.getBoundingClientRect();
          if (rect.top < view!.innerHeight && rect.bottom > 0) lettered.current.add(heading);
          else headingReveals.observe(heading);
        }
        entrance.reconcile();
      }

      const mutations = new view.MutationObserver(() => { needsReconcile = true; schedule(); });
      mutations.observe(root!, { childList: true, subtree: true, attributes: true, attributeFilter: ['data-motion-reveal', 'data-motion-parallax', 'data-motion-stagger', 'data-motion-intensity', 'data-motion-heading', 'data-element-motion', 'data-image-reveal'] });
      const resize = view.ResizeObserver ? new view.ResizeObserver(schedule) : null;
      resize?.observe(root!);
      const focus = (event: FocusEvent) => {
        if (event.target instanceof view.Node) entrance.revealWithin(event.target);
        for (const [element, animation] of animations) {
          if (event.target instanceof view.Node && element.contains(event.target)) {
            animation.cancel();
            animations.delete(element);
          }
        }
      };
      const visibility = () => {
        if (document.hidden && frame) { view.cancelAnimationFrame(frame); frame = 0; }
        else schedule();
      };
      root!.addEventListener('focusin', focus);
      root!.addEventListener('load', schedule, true);
      view.addEventListener('scroll', schedule, { passive: true });
      view.addEventListener('resize', schedule, { passive: true });
      document.addEventListener('visibilitychange', visibility);
      reconcile();
      schedule();

      stop = () => {
        alive = false;
        view.cancelAnimationFrame(frame);
        reveals.disconnect();
        parallax.disconnect();
        headingReveals.disconnect();
        letterRuns.forEach((finish) => finish());
        entrance.stop();
        mutations.disconnect();
        resize?.disconnect();
        animations.forEach((animation) => animation.cancel());
        images.forEach((original, image) => restoreImage(image, original));
        root!.removeEventListener('focusin', focus);
        root!.removeEventListener('load', schedule, true);
        view.removeEventListener('scroll', schedule);
        view.removeEventListener('resize', schedule);
        document.removeEventListener('visibilitychange', visibility);
      };
    };

    configure();
    preference.addEventListener('change', configure);
    return () => { stop(); preference.removeEventListener('change', configure); };
  }, []);

  return <div ref={rootRef} {...props}>{children}</div>;
}
